import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { toast } from 'sonner';
import { loadPomodoroConfig, savePomodoroConfig, PomodoroConfig } from '@/components/PomodoroSettings';

export type PomodoroMode = 'idle' | 'studying' | 'short_break' | 'long_break';

interface RuntimeState {
  mode: PomodoroMode;
  timeLeft: number;
  isRunning: boolean;
  pomodorosCompleted: number;
  soundEnabled: boolean;
  config: PomodoroConfig;
  // Wall-clock anchor: epoch ms when current running segment will end
  endsAt: number | null;
}

interface PomodoroContextValue extends RuntimeState {
  totalSeconds: number;
  completionCounter: number;
  start: () => void;
  pause: () => void;
  reset: () => void;
  toggleSound: () => void;
  updateConfig: (c: PomodoroConfig) => void;
  getStateLabel: () => string;
}

const STORAGE_KEY = 'pomodoro_runtime_state';

const PomodoroContext = createContext<PomodoroContextValue | null>(null);

function getDurationSeconds(mode: PomodoroMode, config: PomodoroConfig): number {
  switch (mode) {
    case 'short_break':
      return config.shortBreakMinutes * 60;
    case 'long_break':
      return config.longBreakMinutes * 60;
    case 'studying':
    case 'idle':
    default:
      return config.studyMinutes * 60;
  }
}

function loadInitial(): RuntimeState {
  const config = loadPomodoroConfig();
  const base: RuntimeState = {
    mode: 'idle',
    timeLeft: config.studyMinutes * 60,
    isRunning: false,
    pomodorosCompleted: 0,
    soundEnabled: true,
    config,
    endsAt: null,
  };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return base;
    const saved = JSON.parse(raw) as RuntimeState;
    // Reconstruct timeLeft if was running
    if (saved.isRunning && saved.endsAt) {
      const remaining = Math.round((saved.endsAt - Date.now()) / 1000);
      if (remaining > 0) {
        return { ...base, ...saved, timeLeft: remaining };
      }
      // Expired while away — keep at 0, not running, so next tick triggers completion
      return { ...base, ...saved, timeLeft: 0, isRunning: false, endsAt: null };
    }
    return { ...base, ...saved, isRunning: false, endsAt: null };
  } catch {
    return base;
  }
}

function playBeep() {
  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 800;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
  } catch {
    // ignore
  }
}

function notify(title: string, body: string) {
  try {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'granted') {
      new Notification(title, { body, icon: '/favicon.ico' });
    }
  } catch {
    // ignore
  }
}

function formatMMSS(seconds: number) {
  const s = Math.max(0, seconds);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m.toString().padStart(2, '0')}:${r.toString().padStart(2, '0')}`;
}

export function PomodoroProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<RuntimeState>(loadInitial);
  const [completionCounter, setCompletionCounter] = useState(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const originalTitle = useRef<string>(typeof document !== 'undefined' ? document.title : '');

  // Persist state
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore
    }
  }, [state]);

  const handleComplete = useCallback(() => {
    setState((prev) => {
      if (prev.soundEnabled) playBeep();

      if (prev.mode === 'studying') {
        const newCount = prev.pomodorosCompleted + 1;
        const nextMode: PomodoroMode =
          newCount % prev.config.pomodorosUntilLongBreak === 0 ? 'long_break' : 'short_break';
        const label = nextMode === 'long_break' ? 'pausa longa 🌟' : 'pausa curta ☕';
        toast.success(`Pomodoro completo! Hora da ${label}`);
        notify('Pomodoro completo! 🎯', `Hora da ${label}.`);
        return {
          ...prev,
          mode: nextMode,
          pomodorosCompleted: newCount,
          timeLeft: getDurationSeconds(nextMode, prev.config),
          isRunning: false,
          endsAt: null,
        };
      }

      // Break ended
      toast.success('Pausa encerrada! Bora focar 🎯');
      notify('Pausa encerrada!', 'Volte ao foco para o próximo pomodoro.');
      return {
        ...prev,
        mode: 'studying',
        timeLeft: getDurationSeconds('studying', prev.config),
        isRunning: false,
        endsAt: null,
      };
    });
    setCompletionCounter((c) => c + 1);
  }, []);

  // Tick loop based on wall clock
  useEffect(() => {
    if (!state.isRunning) {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return;
    }

    const tick = () => {
      setState((prev) => {
        if (!prev.isRunning || prev.endsAt == null) return prev;
        const remaining = Math.max(0, Math.round((prev.endsAt - Date.now()) / 1000));
        return { ...prev, timeLeft: remaining };
      });
    };

    tick();
    tickRef.current = setInterval(tick, 1000);
    return () => {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [state.isRunning]);

  // Trigger completion when timeLeft hits 0 while running
  useEffect(() => {
    if (state.isRunning && state.timeLeft <= 0) {
      handleComplete();
    }
  }, [state.timeLeft, state.isRunning, handleComplete]);

  // Update document title while running
  useEffect(() => {
    const savedTitle = originalTitle.current;
    if (!state.isRunning) {
      document.title = savedTitle;
      return;
    }
    const label =
      state.mode === 'studying' ? 'Foco' : state.mode === 'long_break' ? 'Pausa longa' : 'Pausa';
    document.title = `${formatMMSS(state.timeLeft)} · ${label}`;
    return () => {
      document.title = savedTitle;
    };
  }, [state.timeLeft, state.isRunning, state.mode]);

  const start = useCallback(() => {
    // Request notification permission lazily
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    } catch {
      // ignore
    }
    setState((prev) => {
      const mode: PomodoroMode = prev.mode === 'idle' ? 'studying' : prev.mode;
      const duration = getDurationSeconds(mode, prev.config);
      const timeLeft = prev.timeLeft > 0 && prev.mode !== 'idle' ? prev.timeLeft : duration;
      return {
        ...prev,
        mode,
        timeLeft,
        isRunning: true,
        endsAt: Date.now() + timeLeft * 1000,
      };
    });
  }, []);

  const pause = useCallback(() => {
    setState((prev) => ({ ...prev, isRunning: false, endsAt: null }));
  }, []);

  const reset = useCallback(() => {
    setState((prev) => ({
      ...prev,
      mode: 'idle',
      isRunning: false,
      endsAt: null,
      timeLeft: prev.config.studyMinutes * 60,
      pomodorosCompleted: 0,
    }));
  }, []);

  const toggleSound = useCallback(() => {
    setState((prev) => ({ ...prev, soundEnabled: !prev.soundEnabled }));
  }, []);

  const updateConfig = useCallback((config: PomodoroConfig) => {
    savePomodoroConfig(config);
    setState((prev) => {
      // If idle, recompute timeLeft for new study duration
      if (prev.mode === 'idle') {
        return { ...prev, config, timeLeft: config.studyMinutes * 60 };
      }
      return { ...prev, config };
    });
  }, []);

  const getStateLabel = useCallback(() => {
    switch (state.mode) {
      case 'idle':
        return 'Pronto para começar';
      case 'studying':
        return 'Foco total! 🎯';
      case 'short_break':
        return 'Pausa curta ☕';
      case 'long_break':
        return 'Pausa longa 🌟';
    }
  }, [state.mode]);

  const totalSeconds = getDurationSeconds(state.mode === 'idle' ? 'studying' : state.mode, state.config);

  const value: PomodoroContextValue = {
    ...state,
    totalSeconds,
    completionCounter,
    start,
    pause,
    reset,
    toggleSound,
    updateConfig,
    getStateLabel,
  };

  return <PomodoroContext.Provider value={value}>{children}</PomodoroContext.Provider>;
}

export function usePomodoro() {
  const ctx = useContext(PomodoroContext);
  if (!ctx) throw new Error('usePomodoro must be used within PomodoroProvider');
  return ctx;
}

export function formatPomodoroTime(seconds: number) {
  return formatMMSS(seconds);
}
